import { registerPage } from '@zillow/client-profiler-interface-cs';
import { logError } from '@zg-rentals/log-error';
import { MIN_LG } from '@/src/lib/breakpoints';
import { readZgEnvironment } from '@/src/lib/zgEnv';

export type ClientProfilerPageName =
  | 'hotpads-srp'
  | 'hotpads-hdp'
  | 'hotpads-bdp'
  | 'hotpads-homepage'
  | 'hotpads-city-page'
  | 'hotpads-state-page'
  | 'hotpads-my-list';

export type ClientProfilerPageContext = {
  pageName: ClientProfilerPageName | 'unknown';
  platform: 'desktop' | 'mobile' | 'unknown';
};

const unknownClientProfilerPageContext: ClientProfilerPageContext = {
  pageName: 'unknown',
  platform: 'unknown',
};
let clientProfilerPageContext = unknownClientProfilerPageContext;
let landingClientProfilerPageContext = unknownClientProfilerPageContext;

export function getClientProfilerPageContext(): ClientProfilerPageContext {
  return clientProfilerPageContext;
}

/**
 * The route that served this page load. CrUX attributes a page's whole Core Web Vitals
 * lifetime to the landing URL and does not treat a soft navigation as a new page view, so
 * this is the dimension to tag web-vitals metrics with. Unlike the live context it survives
 * a route change and its unmount cleanup. Stays `unknown` for a metric that finalises before
 * the first route registers.
 */
export function getLandingClientProfilerPageContext(): ClientProfilerPageContext {
  return landingClientProfilerPageContext;
}

export function registerClientProfilerPage(pageName: ClientProfilerPageName) {
  if (typeof window === 'undefined') return;

  const platform = window.matchMedia(MIN_LG).matches ? 'desktop' : 'mobile';
  const registration = { pageName, platform } as const;
  clientProfilerPageContext = registration;
  if (landingClientProfilerPageContext === unknownClientProfilerPageContext) {
    landingClientProfilerPageContext = registration;
  }

  try {
    registerPage({
      pageName,
      // Local dev renders an empty tag; hotpads-web reported 'localhost' there.
      environment: readZgEnvironment() || 'localhost',
      platform,
    });
  } catch (e) {
    logError({
      error: e,
      errorType: 'ClientProfilerRegistrationError',
      context: { pageName },
    });
  }

  return () => {
    if (clientProfilerPageContext === registration) {
      clientProfilerPageContext = unknownClientProfilerPageContext;
    }
  };
}
